import React, { useEffect, useState, useMemo } from 'react';
import {
  Layout,
  Typography,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Tabs,
  Modal,
  Form,
  Select,
  Switch,
  Checkbox,
  message,
  Tooltip,
  Popconfirm,
  Badge,
  Alert,
  Card,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  SafetyOutlined,
  ShopOutlined,
  CodeOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { COUNTRIES, getStatesByCountry } from '../data/locationOptions';
import api from '../config/api';
import { PERMISSION_METADATA_FALLBACK } from '../config/permissionMetadataFallback';
import type { ColumnsType } from 'antd/es/table';
import { Upload } from 'antd';

const { Title, Text } = Typography;
const { Content } = Layout;
const { TabPane } = Tabs;

interface Department {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
}

interface Role {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  level: number;
  permissions?: Record<string, boolean>;
  is_active: boolean;
  is_system: boolean;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'admin' | 'agent' | 'sales_agent' | 'sales_rep_internal' | 'developer' | 'resellers' | 'agent_partner' | 'sales_rep_associated';
  role_display_name?: string;
  company_name?: string;
  country?: string;
  county?: string;
  state?: string;
  parent_agent_partner_id?: string;
  company_id?: string;
  company_ids?: string[];
  extension?: string;
  ringcentral_extension?: string;
  agent_level?: number;
  is_active: boolean;
  can_edit_businesses?: boolean;
  can_manage_replacements?: boolean;
  departments?: Department[];
}

interface Company {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
}

interface CompanyUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  role_display_name?: string;
  linked_via_parent?: boolean;
}

interface CompanyBusiness {
  id: string;
  business_name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  is_active: boolean;
}

function formatBusinessAddress(business: Pick<CompanyBusiness, 'address' | 'city' | 'state' | 'zip_code' | 'country'>): string {
  return [business.address, business.city, business.state, business.zip_code, business.country]
    .filter(Boolean)
    .join(', ');
}

const UsersAntd: React.FC = () => {
  const { user: currentUser } = useAuth();
  const canApproveUserRequests = usePermission('approve_user_requests');
  const canModerateUserRequests = currentUser?.role === 'admin' || canApproveUserRequests;
  const isAdmin = currentUser?.role === 'admin';
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [deptForm] = Form.useForm();
  const [roleForm] = Form.useForm();
  const [companyForm] = Form.useForm();

  // State
  const [activeTab, setActiveTab] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [createUserModal, setCreateUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [createDeptModal, setCreateDeptModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [confirmBulkText, setConfirmBulkText] = useState('');
  
  // Roles state
  const [createRoleModal, setCreateRoleModal] = useState(false);
  const [editRoleModal, setEditRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [viewPermissionsRole, setViewPermissionsRole] = useState<Role | null>(null);
  const [permissionsTabSearch, setPermissionsTabSearch] = useState('');
  const [permissionsModalSearch, setPermissionsModalSearch] = useState('');
  const [createCompanyModal, setCreateCompanyModal] = useState(false);
  const [editCompanyModal, setEditCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyUsersModal, setCompanyUsersModal] = useState(false);
  const [selectedCompanyForUsers, setSelectedCompanyForUsers] = useState<Company | null>(null);
  const [userToAddToCompany, setUserToAddToCompany] = useState<string | undefined>(undefined);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [companyBusinessesModal, setCompanyBusinessesModal] = useState(false);
  const [selectedCompanyForBusinesses, setSelectedCompanyForBusinesses] = useState<Company | null>(null);
  const [businessToAddToCompany, setBusinessToAddToCompany] = useState<string | undefined>(undefined);
  const [businessSearchTerm, setBusinessSearchTerm] = useState('');

  // User Requests state
  const [approveRequestModal, setApproveRequestModal] = useState(false);
  const [rejectRequestModal, setRejectRequestModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [approveForm] = Form.useForm();
  const [tempCredentialsNotice, setTempCredentialsNotice] = useState<{
    open: boolean;
    username?: string;
    temporaryPassword?: string;
    emailError?: string;
  }>({ open: false });

  // State for pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  // State for filters
  const [filters, setFilters] = useState({
    role: undefined,
    agent_level: undefined,
    is_active: undefined,
    company_id: undefined,
  });

  useEffect(() => {
    if (!isAdmin && canModerateUserRequests && activeTab !== '3') {
      setActiveTab('3');
    }
  }, [isAdmin, canModerateUserRequests, activeTab]);

  // Fetch users with pagination and filters
  const { data: usersData, isLoading: loadingUsers } = useQuery(
    ['users', pagination.current, pagination.pageSize, filters, searchTerm],
    () => {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString(),
      });
      
      if (filters.role) params.append('role', filters.role);
      if (filters.agent_level) params.append('agent_level', filters.agent_level);
      if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active));
      if (filters.company_id) params.append('company_id', filters.company_id);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      
      return api.get(`/api/users?${params.toString()}`).then(res => res.data);
    },
    { 
      enabled: currentUser?.role === 'admin',
      keepPreviousData: true
    }
  );

  const users = Array.isArray(usersData?.users) ? usersData.users : [];
  const total = typeof usersData?.total === 'number' ? usersData.total : 0;

  // Fetch departments
  const { data: departmentsData, isLoading: loadingDepts } = useQuery(
    'departments',
    () => api.get('/api/departments').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );

  const departments = Array.isArray(departmentsData?.departments) ? departmentsData.departments : [];

  // Fetch Agent/Partner users for Sales Rep - Associated parent dropdown
  const { data: agentPartnersData } = useQuery(
    ['users', 'agent_partner'],
    () => api.get('/api/users?role=agent_partner&limit=100').then(res => res.data),
    { enabled: (createUserModal || editUserModal) && currentUser?.role === 'admin' }
  );

  // Fetch roles for Permissions tab
  const { data: rolesData, isLoading: loadingRoles } = useQuery(
    'roles',
    () => api.get('/api/roles').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );

  // Fetch companies for Companies tab
  const { data: companiesData, isLoading: loadingCompanies } = useQuery(
    'companies',
    () => api.get('/api/companies').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );

  // Fetch permission metadata for Permissions tab
  const { data: permissionsData } = useQuery(
    'permissions',
    () => api.get('/api/permissions').then(res => res.data),
    {
      enabled: currentUser?.role === 'admin',
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );

  const roles = Array.isArray(rolesData?.roles) ? rolesData.roles : [];
  const companies: Company[] = Array.isArray(companiesData?.companies) ? companiesData.companies : [];
  const filteredCompanies = useMemo(() => {
    const q = companySearchTerm.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => c.name.toLowerCase().includes(q));
  }, [companies, companySearchTerm]);
  const { data: companyUsersData, isLoading: loadingCompanyUsers } = useQuery(
    ['company-users', selectedCompanyForUsers?.id],
    () => api.get(`/api/companies/${selectedCompanyForUsers?.id}/users`).then(res => res.data),
    { enabled: companyUsersModal && !!selectedCompanyForUsers?.id && currentUser?.role === 'admin' }
  );
  const { data: companyBusinessesData, isLoading: loadingCompanyBusinesses } = useQuery(
    ['company-businesses', selectedCompanyForBusinesses?.id],
    () => api.get(`/api/companies/${selectedCompanyForBusinesses?.id}/businesses`).then((res) => res.data),
    { enabled: companyBusinessesModal && !!selectedCompanyForBusinesses?.id && currentUser?.role === 'admin' }
  );

  const companyUsers: CompanyUser[] = Array.isArray(companyUsersData?.users) ? companyUsersData.users : [];
  const companyBusinesses: CompanyBusiness[] = Array.isArray(companyBusinessesData?.businesses)
    ? companyBusinessesData.businesses
    : [];

  const { data: companyUserPickerData, isLoading: loadingCompanyUserPicker, isError: companyUserPickerError } = useQuery(
    ['users-company-picker', userSearchTerm],
    () => {
      const params = new URLSearchParams({ limit: '100', page: '1' });
      if (userSearchTerm.trim()) params.append('search', userSearchTerm.trim());
      return api.get(`/api/users?${params.toString()}`).then((res) => res.data);
    },
    { enabled: companyUsersModal && isAdmin, keepPreviousData: true }
  );

  const { data: businessSearchData, isLoading: loadingBusinessSearch } = useQuery(
    ['business-search-company-picker', businessSearchTerm],
    () =>
      api
        .get(`/api/businesses/search?search=${encodeURIComponent(businessSearchTerm.trim())}&limit=50`)
        .then((res) => res.data),
    { enabled: companyBusinessesModal && isAdmin && businessSearchTerm.trim().length >= 2 }
  );

  const companyBusinessPickerOptions = useMemo(() => {
    const results: CompanyBusiness[] = Array.isArray(businessSearchData?.businesses)
      ? businessSearchData.businesses
      : [];
    const linkedIds = new Set(companyBusinesses.map((b) => b.id));
    return results
      .filter((b) => !linkedIds.has(b.id))
      .map((b) => ({
        value: b.id,
        label: b.business_name,
        address: formatBusinessAddress(b),
      }));
  }, [businessSearchData, companyBusinesses]);

  const permissionList = useMemo(() => {
    const api = Array.isArray(permissionsData?.permissions) ? permissionsData.permissions : [];
    const keys = new Set(api.map((p: { key: string }) => p.key));
    const merged = [...api];
    for (const p of PERMISSION_METADATA_FALLBACK) {
      if (!keys.has(p.key)) merged.push(p);
    }
    return merged;
  }, [permissionsData]);

  const filterPermissions = (items: typeof permissionList, qRaw: string) => {
    const q = qRaw.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (p: { key: string; label: string }) =>
        p.label.toLowerCase().includes(q) || p.key.toLowerCase().includes(q)
    );
  };

  const filteredPermissionTabList = useMemo(
    () => filterPermissions(permissionList, permissionsTabSearch),
    [permissionList, permissionsTabSearch]
  );

  const filteredPermissionModalList = useMemo(
    () => filterPermissions(permissionList, permissionsModalSearch),
    [permissionList, permissionsModalSearch]
  );

  useEffect(() => {
    if (!viewPermissionsRole?.id || !rolesData) return;
    const fresh = roles.find((r: Role) => r.id === viewPermissionsRole.id);
    if (!fresh) return;
    setViewPermissionsRole((prev) => {
      if (!prev || prev.id !== fresh.id) return prev;
      if (JSON.stringify(prev.permissions ?? {}) === JSON.stringify(fresh.permissions ?? {})) return prev;
      return fresh;
    });
  }, [rolesData, roles, viewPermissionsRole?.id]);

  const agentPartnerUsers = (Array.isArray(agentPartnersData?.users) ? agentPartnersData.users : []) as User[];
  const companyNameById: Record<string, string> = {};
  companies.forEach((company) => {
    companyNameById[company.id] = company.name;
  });

  const getLinkedCompanyIds = (u: User) => {
    if (u.company_ids && u.company_ids.length > 0) return u.company_ids;
    if (u.company_id) return [u.company_id];
    return [];
  };

  const getParentCompanyName = (u: User) => {
    const ids = getLinkedCompanyIds(u);
    if (!ids.length) return undefined;
    const names = ids.map((id) => companyNameById[id]).filter(Boolean);
    return names.length ? names.join(', ') : undefined;
  };

  const getParentCompanyOptionLabel = (u: User) => {
    const companyName = getParentCompanyName(u);
    if (!companyName) return undefined;
    return `${companyName} — ${u.first_name} ${u.last_name}`;
  };

  const getUserRoleLabel = (u: User) => {
    if (u.role_display_name) return u.role_display_name;
    const role = roles.find((r: Role) => r.name === u.role);
    return role?.display_name || u.role || 'Unknown';
  };

  const getUserAssociatedCompanyLabel = (u: User) => {
    const linkedCompanies = getParentCompanyName(u);
    if (linkedCompanies) return linkedCompanies;
    return u.company_name?.trim() || undefined;
  };

  const formatCompanyUserPickerLabel = (u: User) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
    const parts = [`${name} (${u.email})`, getUserRoleLabel(u)];
    const company = getUserAssociatedCompanyLabel(u);
    if (company) parts.push(company);
    return parts.join(' — ');
  };

  const companyUserPickerOptions = useMemo(() => {
    const allUsers: User[] = Array.isArray(companyUserPickerData?.users) ? companyUserPickerData.users : [];
    const linkedIds = new Set(companyUsers.map((u) => u.id));
    return allUsers
      .filter((u) => !linkedIds.has(u.id))
      .map((u) => ({
        value: u.id,
        label: formatCompanyUserPickerLabel(u),
      }));
  }, [companyUserPickerData, companyUsers, roles, companies]);

  const parentCompanyOptions = agentPartnerUsers
    .map((u) => {
      const label = getParentCompanyOptionLabel(u);
      if (!label) return null;
      return {
        value: u.id,
        label,
      };
    })
    .filter(Boolean) as Array<{ value: string; label: string }>;

  // Permissions tab: fetch all users for dropdown (load on Users page so data is ready)
  const { data: allUsersData, isLoading: loadingAllUsers } = useQuery(
    ['users', 'permissions-dropdown'],
    () => api.get('/api/users?limit=100').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );
  const allUsersForPermissions = Array.isArray(allUsersData?.users)
    ? allUsersData.users.filter((u: User) => u.role !== 'admin')
    : [];

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  // Fetch user requests
  const { data: requestsData, isLoading: loadingRequests, error: requestsError, refetch: refetchRequests } = useQuery(
    'user-requests',
    async () => {
      try {
        const res = await api.get('/api/users/requests');
        return res.data;
      } catch (error: any) {
        console.error('Error fetching user requests:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        throw error;
      }
    },
    {
      enabled: canModerateUserRequests,
      refetchInterval: 30000, // Refetch every 30 seconds
      retry: false, // Don't retry on error to avoid spam
    }
  );

  // Fetch pending count
  const { data: pendingCountData, error: countError } = useQuery(
    'user-requests-pending-count',
    () => api.get('/api/users/requests/pending/count').then(res => res.data),
    {
      enabled: canModerateUserRequests,
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Error fetching pending count:', error);
      },
    }
  );

  // Extract requests array - handle both { requests: [...] } and direct array
  const requests = Array.isArray(requestsData) ? requestsData : (requestsData?.requests || []);
  const pendingRequests = requests.filter((r: any) => r.status === 'pending');
  const pendingCount = pendingCountData?.count ?? pendingRequests.length;
  
  // Debug logging (remove in production)
  if (canModerateUserRequests) {
    console.log('Requests data:', requestsData);
    console.log('Requests array:', requests);
    console.log('Pending count data:', pendingCountData);
    console.log('Pending count:', pendingCount);
    if (requestsError) console.error('Requests error:', requestsError);
    if (countError) console.error('Count error:', countError);
  }

  // Mutations
  const createUserMutation = useMutation(
    (userData: any) => api.post('/api/users', userData),
    {
      onSuccess: (response: any) => {
        const data = response?.data || {};
        queryClient.invalidateQueries('users');
        if (data.credentials_email_sent === true) {
          message.success('User created and credentials email sent successfully');
        } else if (data.temporary_password) {
          message.warning('User created, but credentials need to be shared manually');
          setTempCredentialsNotice({
            open: true,
            username: data.username || data.email,
            temporaryPassword: data.temporary_password,
            emailError: data.credentials_email_error
          });
        } else {
          message.success('User created successfully');
        }
        setCreateUserModal(false);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create user');
      }
    }
  );

  const updateUserMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => api.put(`/api/users/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('User updated successfully');
        setEditUserModal(false);
        setSelectedUser(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update user');
      }
    }
  );

  const deleteUserMutation = useMutation(
    (id: string) => api.delete(`/api/users/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('User deleted successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete user');
      }
    }
  );

  // Role mutations
  const createRoleMutation = useMutation(
    (roleData: any) => api.post('/api/roles', roleData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles');
        message.success('Role created successfully');
        setCreateRoleModal(false);
        roleForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create role');
      }
    }
  );

  const updateRoleMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => api.put(`/api/roles/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles');
        message.success('Role updated successfully');
        setEditRoleModal(false);
        setSelectedRole(null);
        roleForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update role');
      }
    }
  );

  const deleteRoleMutation = useMutation(
    (id: string) => api.delete(`/api/roles/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles');
        message.success('Role deleted successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete role');
      }
    }
  );

  // Company mutations
  const createCompanyMutation = useMutation(
    (companyData: any) => api.post('/api/companies', companyData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companies');
        message.success('Company created successfully');
        setCreateCompanyModal(false);
        companyForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create company');
      }
    }
  );

  const updateCompanyMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => api.put(`/api/companies/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companies');
        message.success('Company updated successfully');
        setEditCompanyModal(false);
        setSelectedCompany(null);
        companyForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update company');
      }
    }
  );

  const deleteCompanyMutation = useMutation(
    (id: string) => api.delete(`/api/companies/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companies');
        message.success('Company deleted successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete company');
      }
    }
  );

  const addCompanyUserMutation = useMutation(
    ({ companyId, userId }: { companyId: string; userId: string }) =>
      api.post(`/api/companies/${companyId}/users`, { user_id: userId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['company-users', selectedCompanyForUsers?.id]);
        queryClient.invalidateQueries('users');
        message.success('User added to company');
        setUserToAddToCompany(undefined);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to add user to company');
      },
    }
  );

  const removeCompanyUserMutation = useMutation(
    ({ companyId, userId }: { companyId: string; userId: string }) =>
      api.delete(`/api/companies/${companyId}/users/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['company-users', selectedCompanyForUsers?.id]);
        queryClient.invalidateQueries('users');
        message.success('User removed from company');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to remove user from company');
      },
    }
  );

  const addCompanyBusinessMutation = useMutation(
    ({ companyId, businessId }: { companyId: string; businessId: string }) =>
      api.post(`/api/companies/${companyId}/businesses`, { business_id: businessId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['company-businesses', selectedCompanyForBusinesses?.id]);
        queryClient.invalidateQueries('businesses');
        message.success('Business added to company');
        setBusinessToAddToCompany(undefined);
        setBusinessSearchTerm('');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to add business to company');
      },
    }
  );

  const removeCompanyBusinessMutation = useMutation(
    ({ companyId, businessId }: { companyId: string; businessId: string }) =>
      api.delete(`/api/companies/${companyId}/businesses/${businessId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['company-businesses', selectedCompanyForBusinesses?.id]);
        queryClient.invalidateQueries('businesses');
        message.success('Business removed from company');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to remove business from company');
      },
    }
  );

  const [passwordResetConfirm, setPasswordResetConfirm] = useState<{ open: boolean; user: User | null }>({
    open: false,
    user: null,
  });

  const sendPasswordResetMutation = useMutation(
    (userId: string) => api.post('/api/auth/admin/send-password-reset', { user_id: userId }),
    {
      onSuccess: (response: any) => {
        const data = response.data;
        setPasswordResetConfirm({ open: false, user: null });
        if (data.emailSent) {
          message.success(`Password reset link sent to ${data.message.split(' ').pop()}`);
        } else {
          // Show warning with reset link if email failed
          const resetLink = data.resetLink;
          const errorMsg = data.emailError || 'Email could not be sent';
          
          message.warning({
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Password reset link generated but email could not be sent.</strong>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Reason:</strong> {errorMsg}
                </div>
                {resetLink && (
                  <div>
                    <strong>Reset Link:</strong>
                    <div style={{ 
                      marginTop: 4, 
                      padding: '8px', 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: '4px',
                      wordBreak: 'break-all',
                      fontSize: '12px',
                      fontFamily: 'monospace'
                    }}>
                      {resetLink}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Button 
                        size="small" 
                        type="link"
                        onClick={() => {
                          navigator.clipboard.writeText(resetLink);
                          message.success('Reset link copied to clipboard!');
                        }}
                      >
                        Copy Link
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ),
            duration: 10, // Show for 10 seconds so admin can copy the link
          });
          
          // Also log to console for easy access
          console.warn('Password reset link (email failed):', resetLink);
        }
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.error || 'Failed to send password reset link';
        message.error(errorMessage);
        console.error('Password reset error:', error);
      }
    }
  );

  const handleSendPasswordReset = (user: User) => {
    setPasswordResetConfirm({ open: true, user });
  };

  const confirmSendPasswordReset = () => {
    if (passwordResetConfirm.user) {
      sendPasswordResetMutation.mutate(passwordResetConfirm.user.id);
    }
  };

  // User Requests mutations
  const approveRequestMutation = useMutation(
    ({ requestId, userData }: { requestId: string; userData: any }) =>
      api.post(`/api/users/requests/${requestId}/approve`, userData),
    {
      onSuccess: () => {
        message.success('User request approved and account created successfully');
        setApproveRequestModal(false);
        approveForm.resetFields();
        setSelectedRequest(null);
        queryClient.invalidateQueries('user-requests');
        queryClient.invalidateQueries('user-requests-pending-count');
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to approve user request');
      }
    }
  );

  const rejectRequestMutation = useMutation(
    ({ requestId, reason }: { requestId: string; reason?: string }) =>
      api.post(`/api/users/requests/${requestId}/reject`, { rejection_reason: reason }),
    {
      onSuccess: () => {
        message.success('User request rejected');
        setRejectRequestModal(false);
        setSelectedRequest(null);
        queryClient.invalidateQueries('user-requests');
        queryClient.invalidateQueries('user-requests-pending-count');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to reject user request');
      }
    }
  );

  const handleApproveRequest = (request: any) => {
    setSelectedRequest(request);
    approveForm.resetFields();
    // Pre-fill form with request data
    approveForm.setFieldsValue({
      first_name: request.first_name,
      last_name: request.last_name,
      email: request.email,
      phone: request.phone,
    });
    setApproveRequestModal(true);
  };

  const handleRejectRequest = (request: any) => {
    setSelectedRequest(request);
    setRejectRequestModal(true);
  };

  const handleCreateRole = (values: any) => {
    const name = String(values.name || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z_]/g, '_').replace(/_+/g, '_') || 'role';
    createRoleMutation.mutate({
      name,
      display_name: values.display_name.trim(),
      description: values.description?.trim() || undefined,
      level: 0,
      is_active: values.is_active !== false,
    });
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    roleForm.setFieldsValue({
      display_name: role.display_name,
      description: role.description || '',
      level: role.level,
      is_active: role.is_active,
    });
    setEditRoleModal(true);
  };

  const handleUpdateRole = (values: any) => {
    if (!selectedRole) return;
    updateRoleMutation.mutate({
      id: selectedRole.id,
      data: {
        display_name: values.display_name.trim(),
        description: values.description?.trim() || undefined,
        is_active: values.is_active !== false,
      },
    });
  };

  const handleDeleteRole = (role: Role) => {
    deleteRoleMutation.mutate(role.id);
  };

  const handleCreateCompany = (values: any) => {
    createCompanyMutation.mutate({
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      is_active: values.is_active !== false,
    });
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    companyForm.setFieldsValue({
      name: company.name,
      description: company.description || '',
      is_active: company.is_active,
    });
    setEditCompanyModal(true);
  };

  const handleUpdateCompany = (values: any) => {
    if (!selectedCompany) return;
    updateCompanyMutation.mutate({
      id: selectedCompany.id,
      data: {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        is_active: values.is_active !== false,
      },
    });
  };

  const handleViewCompanyUsers = (company: Company) => {
    setSelectedCompanyForUsers(company);
    setUserToAddToCompany(undefined);
    setUserSearchTerm('');
    setCompanyUsersModal(true);
  };

  const handleViewCompanyBusinesses = (company: Company) => {
    setSelectedCompanyForBusinesses(company);
    setBusinessToAddToCompany(undefined);
    setBusinessSearchTerm('');
    setCompanyBusinessesModal(true);
  };

  const handleAddUserToCompany = () => {
    if (!selectedCompanyForUsers?.id || !userToAddToCompany) return;
    addCompanyUserMutation.mutate({ companyId: selectedCompanyForUsers.id, userId: userToAddToCompany });
  };

  const handleRemoveUserFromCompany = (userId: string) => {
    if (!selectedCompanyForUsers?.id) return;
    removeCompanyUserMutation.mutate({ companyId: selectedCompanyForUsers.id, userId });
  };

  const handleAddBusinessToCompany = () => {
    if (!selectedCompanyForBusinesses?.id || !businessToAddToCompany) return;
    addCompanyBusinessMutation.mutate({
      companyId: selectedCompanyForBusinesses.id,
      businessId: businessToAddToCompany,
    });
  };

  const handleRemoveBusinessFromCompany = (businessId: string) => {
    if (!selectedCompanyForBusinesses?.id) return;
    removeCompanyBusinessMutation.mutate({ companyId: selectedCompanyForBusinesses.id, businessId });
  };

  // Update role permissions mutation
  const updatePermissionsMutation = useMutation(
    ({ roleId, permissions }: { roleId: string; permissions: Record<string, boolean> }) =>
      api.put(`/api/roles/${roleId}/permissions`, { permissions }),
    {
      onSuccess: () => {
        message.success('Role permissions updated');
        queryClient.invalidateQueries('roles');
        queryClient.invalidateQueries('user-permissions');
        queryClient.invalidateQueries('permissions');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update permissions');
      }
    }
  );

  const handlePermissionToggle = (roleId: string, permissionKey: string, currentPermissions: Record<string, boolean>, checked: boolean) => {
    const next = { ...(currentPermissions || {}), [permissionKey]: checked };
    updatePermissionsMutation.mutate({ roleId, permissions: next });
  };

  // Fetch selected user's permissions
  const { data: selectedUserPerms, isLoading: loadingUserPerms } = useQuery(
    ['user-permissions', selectedUserId],
    () => api.get(`/api/users/${selectedUserId}/permissions`).then(res => res.data),
    { enabled: !!selectedUserId }
  );

  const updateUserPermissionsMutation = useMutation(
    ({ userId, effective }: { userId: string; effective: Record<string, boolean> }) =>
      api.put(`/api/users/${userId}/permissions`, { effective_permissions: effective }),
    {
      onSuccess: () => {
        message.success('User permissions updated');
        queryClient.invalidateQueries(['user-permissions', selectedUserId]);
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries('permissions');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update permissions');
      }
    }
  );

  const handleUserPermissionChange = (key: string, checked: boolean) => {
    if (!selectedUserId || !selectedUserPerms) return;
    const next = { ...(selectedUserPerms.effective_permissions || {}), [key]: checked };
    updateUserPermissionsMutation.mutate({ userId: selectedUserId, effective: next });
  };

  const confirmApproveRequest = (values: any) => {
    if (selectedRequest) {
      approveRequestMutation.mutate({
        requestId: selectedRequest.id,
        userData: values
      });
    }
  };

  const confirmRejectRequest = (reason?: string) => {
    if (selectedRequest) {
      rejectRequestMutation.mutate({
        requestId: selectedRequest.id,
        reason
      });
    }
  };

  const bulkDeleteUsers = useMutation(
    async (ids: string[]) => {
      await api.post('/api/users/bulk-delete', { ids });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('Selected users deleted');
        setSelectedRowKeys([]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to bulk delete users');
      }
    }
  );

  const createDeptMutation = useMutation(
    (deptData: any) => api.post('/api/departments', deptData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('departments');
        message.success('Department created successfully');
        setCreateDeptModal(false);
        deptForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create department');
      }
    }
  );

  // Table columns for users
  const userColumns: ColumnsType<User> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Space>
          {record.role === 'admin' ? (
            <SafetyOutlined style={{ color: '#f44336' }} />
          ) : record.role === 'sales_agent' || record.role === 'sales_rep_internal' ? (
            <ShopOutlined style={{ color: '#ff9800' }} />
          ) : record.role === 'developer' ? (
            <CodeOutlined style={{ color: '#9c27b0' }} />
          ) : (
            <UserOutlined style={{ color: '#1976d2' }} />
          )}
          <span style={{ fontWeight: 600 }}>
            {record.first_name} {record.last_name}
          </span>
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => {
        const colors: Record<string, string> = {
          admin: 'red',
          agent: 'blue',
          sales_agent: 'orange',
          sales_rep_internal: 'orange',
          developer: 'purple',
          resellers: 'cyan',
          agent_partner: 'geekblue',
          sales_rep_associated: 'lime',
        };
        const labels: Record<string, string> = {
          sales_rep_internal: 'Sales Rep - Internal',
          sales_rep_associated: 'Sales Rep - Associated',
          agent_partner: 'Agent/Partner',
          resellers: 'Resellers',
        };
        const label = labels[role] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return (
          <Space size={4} wrap>
            <Tag color={colors[role] || 'default'}>
              {label}
            </Tag>
            {role === 'agent_partner' && (
              <Tag color="gold">Company Scoped</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Agent Level',
      dataIndex: 'agent_level',
      key: 'agent_level',
      width: 100,
      align: 'center',
      render: (level, record: User) => record.role === 'agent' ? (level || '-') : '—',
    },
    {
      title: 'Departments',
      key: 'departments',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const depts = record.departments || [];
        if (depts.length === 0) return '-';
        
        const deptNames = depts.map(d => d.name).join(', ');
        return (
          <Tooltip title={deptNames}>
            <Badge count={depts.length} showZero={false} style={{ backgroundColor: '#1976d2' }} />
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title={currentUser?.role === 'admin' ? 'Send Password Reset Link' : 'Admin only'}>
            <Button
              type="text"
              icon={<LockOutlined />}
              onClick={() => handleSendPasswordReset(record)}
              disabled={currentUser?.role !== 'admin' || !record.email}
              size="small"
            />
          </Tooltip>
          <Tooltip title={currentUser?.role === 'admin' ? 'Delete' : 'Admin only'}>
            <Popconfirm
              title="Delete user"
              description="Are you sure you want to delete this user?"
              onConfirm={() => deleteUserMutation.mutate(record.id)}
              disabled={currentUser?.role !== 'admin'}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={currentUser?.role !== 'admin'}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Department columns
  const deptColumns: ColumnsType<Department> = [
    {
      title: 'Department Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc) => desc || '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
  ];

  // Since search is now server-side, we don't need client-side filtering
  // But we keep this for any additional client-side filtering if needed
  const filteredUsers = users;

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    form.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      agent_level: user.agent_level,
      department_ids: (user.departments || []).map(d => d.id),
      ringcentral_extension: user.ringcentral_extension,
      is_active: user.is_active,
      company_name: user.company_name,
      country: user.country,
      county: user.county,
      state: user.state,
      parent_agent_partner_id: user.parent_agent_partner_id,
      company_id: user.company_id,
      company_ids:
        user.company_ids && user.company_ids.length > 0
          ? user.company_ids
          : user.company_id
            ? [user.company_id]
            : [],
    });
    setEditUserModal(true);
  };

  const ROLES_NO_DEPT_OR_RINGCENTRAL = ['resellers', 'agent_partner', 'sales_rep_internal'];

  const handleCreateUser = (values: any) => {
    const payload = { ...values };
    if (ROLES_NO_DEPT_OR_RINGCENTRAL.includes(values.role)) {
      payload.department_ids = [];
      payload.ringcentral_extension = undefined;
    }
    if (values.role !== 'agent') payload.agent_level = null;
    if (Array.isArray(payload.company_ids) && payload.company_ids.length > 0) {
      delete payload.company_id;
    }
    createUserMutation.mutate(payload);
  };

  const handleUpdateUser = (values: any) => {
    if (selectedUser) {
      const { password, ...data } = values;
      let updateData = password ? values : data;
      if (ROLES_NO_DEPT_OR_RINGCENTRAL.includes(updateData.role)) {
        updateData = { ...updateData, department_ids: [], ringcentral_extension: undefined };
      }
      if (updateData.role !== 'agent') updateData.agent_level = null;
      if (Array.isArray(updateData.company_ids) && updateData.company_ids.length > 0) {
        delete updateData.company_id;
      }
      updateUserMutation.mutate({ id: selectedUser.id, data: updateData });
    }
  };

  const handleCreateDept = (values: any) => {
    createDeptMutation.mutate(values);
  };

  if (!canModerateUserRequests) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#fff' }}>
        <Content style={{ padding: 24 }}>
          <Title level={4}>Access Denied</Title>
          <p>You don't have permission to view this page.</p>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <Content style={{ padding: 24 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>Users</Title>
          <Space>
            {isAdmin && activeTab === '1' && (
              <>
                <Button icon={<DownloadOutlined />}>Export</Button>
                <Button danger disabled={selectedRowKeys.length === 0} onClick={() => { setConfirmBulkText(''); setConfirmBulkOpen(true); }}>Bulk Delete</Button>
                <Button icon={<UploadOutlined />} onClick={() => setBulkUploadOpen(true)}>Bulk Upload</Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.resetFields();
                    form.setFieldsValue({ role: 'agent', send_credentials: true });
                    setCreateUserModal(true);
                  }}
                >
                  Add User
                </Button>
              </>
            )}
            {isAdmin && activeTab === '2' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateDeptModal(true)}>
                Add Department
              </Button>
            )}
            {isAdmin && activeTab === '5' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { roleForm.resetFields(); setCreateRoleModal(true); }}>
                Add Role
              </Button>
            )}
            {isAdmin && activeTab === '6' && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  companyForm.resetFields();
                  companyForm.setFieldsValue({ is_active: true });
                  setCreateCompanyModal(true);
                }}
              >
                Add Company
              </Button>
            )}
          </Space>
        </div>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {isAdmin && (
          <TabPane tab={`User Management (${users.length})`} key="1">
            {/* Search */}
            <Input
              placeholder="Search users by name, email, phone..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              style={{ marginBottom: 16, maxWidth: 400 }}
              allowClear
            />

            {/* Filter Controls */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Select
                placeholder="Filter by Role"
                value={filters.role}
                onChange={(value) => setFilters(prev => ({ ...prev, role: value }))}
                style={{ width: 180 }}
                allowClear
                loading={loadingRoles}
                showSearch
                optionFilterProp="label"
                options={roles.map((r: Role) => ({ value: r.name, label: r.display_name || r.name }))}
              />

              <Select
                placeholder="Agent Level (1-3)"
                value={filters.agent_level}
                onChange={(value) => setFilters(prev => ({ ...prev, agent_level: value }))}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value="1">Level 1</Select.Option>
                <Select.Option value="2">Level 2</Select.Option>
                <Select.Option value="3">Level 3</Select.Option>
              </Select>

              <Select
                placeholder="Filter by Status"
                value={filters.is_active}
                onChange={(value) => setFilters(prev => ({ ...prev, is_active: value }))}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>

              <Select
                placeholder="Filter by Company"
                value={filters.company_id}
                onChange={(value) => {
                  setFilters(prev => ({ ...prev, company_id: value }));
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                style={{ width: 240 }}
                allowClear
                showSearch
                optionFilterProp="label"
                loading={loadingCompanies}
                options={companies.map((c: Company) => ({
                  value: c.id,
                  label: c.is_active ? c.name : `${c.name} (Inactive)`,
                }))}
              />

              <Button 
                onClick={() => {
                  setFilters({ role: undefined, agent_level: undefined, is_active: undefined, company_id: undefined });
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                type="default"
              >
                Clear Filters
              </Button>
            </div>

              {/* Users Table */}
            <Table
                    columns={userColumns}
              dataSource={filteredUsers}
              rowKey="id"
              loading={loadingUsers}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                preserveSelectedRowKeys: true,
              }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize || prev.pageSize,
                  }));
                },
                onShowSizeChange: (current, size) => {
                  setPagination(prev => ({
                    ...prev,
                    current: 1,
                    pageSize: size,
                  }));
                },
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>
          )}

          <TabPane 
            tab={
              <span>
                User Requests
                {pendingCount > 0 && (
                  <Badge count={pendingCount} offset={[8, 0]} />
                )}
              </span>
            } 
            key="3"
          >
            <Table
              columns={[
                {
                  title: 'Name',
                  key: 'name',
                  render: (_, record: any) => `${record.first_name} ${record.last_name}`,
                },
                {
                  title: 'Email',
                  dataIndex: 'email',
                  key: 'email',
                },
                {
                  title: 'Phone',
                  dataIndex: 'phone',
                  key: 'phone',
                  render: (phone) => phone || '-',
                },
                {
                  title: 'Notes',
                  dataIndex: 'notes',
                  key: 'notes',
                  render: (notes) => notes || '-',
                  ellipsis: true,
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status: string) => (
                    <Tag color={status === 'pending' ? 'orange' : status === 'approved' ? 'green' : 'red'}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Tag>
                  ),
                },
                {
                  title: 'Requested',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (date: string) => new Date(date).toLocaleDateString(),
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: (_: any, record: any) => (
                    <Space>
                      {record.status === 'pending' && (
                        <>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleApproveRequest(record)}
                          >
                            Approve
                          </Button>
                          <Button
                            danger
                            size="small"
                            onClick={() => handleRejectRequest(record)}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {record.status !== 'pending' && record.reviewer_first_name && (
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                          Reviewed by {record.reviewer_first_name} {record.reviewer_last_name}
                        </span>
                      )}
                    </Space>
                  ),
                },
              ]}
              dataSource={requests}
              rowKey="id"
              loading={loadingRequests}
              locale={{
                emptyText: requestsError ? (
                  <div>
                    <div style={{ color: '#ff4d4f', marginBottom: 8 }}>Error loading requests</div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      {(requestsError as any)?.response?.data?.error || (requestsError as any)?.message || 'Unknown error'}
                    </div>
                    <Button 
                      size="small" 
                      type="link" 
                      onClick={() => refetchRequests()}
                      style={{ marginTop: 8 }}
                    >
                      Retry
                    </Button>
                  </div>
                ) : 'No requests found'
              }}
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} requests`,
              }}
            />
          </TabPane>

          {isAdmin && (
          <TabPane tab={`Departments (${departments.length})`} key="2">
            <Table
              columns={deptColumns}
              dataSource={departments}
              rowKey="id"
              loading={loadingDepts}
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} departments`,
              }}
            />
          </TabPane>
          )}

          {isAdmin && (
          <TabPane
            tab={
              <span>
                <SafetyCertificateOutlined style={{ marginRight: 4 }} />
                Permissions
              </span>
            }
            key="4"
          >
            <Card>
              {/* By Role */}
              <div style={{ marginBottom: 32 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>By Role</Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Assign permissions to a role. Everyone with that role gets these permissions. Toggle a switch or click On/Off to change.
                </Text>
                <Select
                  placeholder="Select role..."
                  allowClear
                  loading={loadingRoles}
                  style={{ width: '100%', maxWidth: 400, marginBottom: 16 }}
                  value={selectedRoleId || undefined}
                  onChange={(v) => setSelectedRoleId(v || null)}
                  options={roles.filter((r: Role) => r.name !== 'admin').map((r: Role) => ({
                    value: r.id,
                    label: `${r.display_name} (${r.name})`,
                  }))}
                  notFoundContent={loadingRoles ? 'Loading...' : 'No roles found'}
                />
                {selectedRoleId && (() => {
                  const selRole = roles.find((r: Role) => r.id === selectedRoleId);
                  if (!selRole) return null;
                  return (
                    <div style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
                      <Input
                        allowClear
                        placeholder="Filter permissions…"
                        value={permissionsTabSearch}
                        onChange={(e) => setPermissionsTabSearch(e.target.value)}
                        style={{ marginBottom: 16, maxWidth: 400 }}
                        prefix={<SearchOutlined />}
                      />
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                        {filteredPermissionTabList.map((p: { key: string; label: string }) => (
                          <div
                            key={p.key}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 12,
                              padding: '8px 12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: 8,
                              cursor: updatePermissionsMutation.isLoading ? 'wait' : 'pointer',
                              minHeight: 48,
                            }}
                            onClick={() => {
                              if (updatePermissionsMutation.isLoading) return;
                              const nextChecked = !(selRole.permissions?.[p.key] === true);
                              handlePermissionToggle(selRole.id, p.key, selRole.permissions || {}, nextChecked);
                            }}
                            role="presentation"
                          >
                            <Text style={{ flex: 1 }}>{p.label}</Text>
                            <Switch
                              checked={selRole.permissions?.[p.key] === true}
                              onChange={(checked) =>
                                handlePermissionToggle(selRole.id, p.key, selRole.permissions || {}, checked)}
                              disabled={updatePermissionsMutation.isLoading}
                              onClick={(_, e) => e.stopPropagation()}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* By User */}
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>By User</Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Set effective permissions for this user (role defaults plus overrides). Server stores only differences from the role.
                </Text>
                <Select
                  placeholder="Search by name or email..."
                  showSearch
                  allowClear
                  loading={loadingAllUsers}
                  style={{ width: '100%', maxWidth: 480 }}
                  value={selectedUserId || undefined}
                  onChange={(v) => setSelectedUserId(v || null)}
                  optionFilterProp="label"
                  filterOption={(input, opt) =>
                    String(opt?.label ?? '').toLowerCase().includes((input || '').toLowerCase())
                  }
                  options={allUsersForPermissions.map((u: User) => ({
                    value: u.id,
                    label: `${u.first_name} ${u.last_name} (${u.email}) — ${u.role}`
                  }))}
                  notFoundContent={loadingAllUsers ? 'Loading users...' : 'No users found'}
                />
              </div>

              {selectedUserId && (
                loadingUserPerms ? (
                  <div style={{ padding: 48, textAlign: 'center' }}>
                    <Spin size="large" />
                  </div>
                ) : selectedUserPerms ? (
                  <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                      {selectedUserPerms.user_name} <Tag>{selectedUserPerms.role}</Tag>
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
                      User-level overrides. Admins always have full access.
                    </Text>
                    <Input
                      allowClear
                      placeholder="Filter permissions…"
                      value={permissionsTabSearch}
                      onChange={(e) => setPermissionsTabSearch(e.target.value)}
                      style={{ marginBottom: 16, maxWidth: 400 }}
                      prefix={<SearchOutlined />}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                      {filteredPermissionTabList.map((p: { key: string; label: string }) => (
                        <div
                          key={p.key}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 12,
                            padding: '8px 12px',
                            border: '1px solid #f0f0f0',
                            borderRadius: 8,
                            cursor: updateUserPermissionsMutation.isLoading ? 'wait' : 'pointer',
                            minHeight: 48,
                          }}
                          onClick={() => {
                            if (updateUserPermissionsMutation.isLoading) return;
                            const nextChecked = !(selectedUserPerms.effective_permissions?.[p.key] === true);
                            handleUserPermissionChange(p.key, nextChecked);
                          }}
                          role="presentation"
                        >
                          <Text style={{ flex: 1 }}>{p.label}</Text>
                          <Switch
                            checked={selectedUserPerms.effective_permissions?.[p.key] === true}
                            onChange={(checked) => handleUserPermissionChange(p.key, checked)}
                            disabled={updateUserPermissionsMutation.isLoading}
                            onClick={(_, e) => e.stopPropagation()}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              )}

              {!selectedUserId && !selectedRoleId && (
                <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>Select a role or user above to assign permissions.</Text>
              )}
            </Card>
          </TabPane>
          )}

          <TabPane
            tab={
              <span>
                <TeamOutlined style={{ marginRight: 4 }} />
                Roles ({roles.length})
              </span>
            }
            key="5"
          >
            <Table
              columns={[
                { title: 'Display Name', dataIndex: 'display_name', key: 'display_name', render: (v: string, r: Role) => <Text strong>{v}</Text> },
                { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string) => <Text type="secondary" style={{ fontFamily: 'monospace' }}>{v}</Text> },
                {
                  title: 'Active',
                  dataIndex: 'is_active',
                  key: 'is_active',
                  width: 80,
                  render: (v: boolean) => v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 260,
                  render: (_: any, r: Role) => (
                    <Space>
                      <Button type="link" size="small" onClick={() => {
                        setPermissionsModalSearch('');
                        setViewPermissionsRole(r);
                      }}>
                        View Permissions
                      </Button>
                      <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditRole(r)}>
                        Edit
                      </Button>
                      {!r.is_system && (
                        <Popconfirm
                          title="Delete this role?"
                          description="Users must be reassigned before deleting."
                          onConfirm={() => handleDeleteRole(r)}
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                        >
                          <Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
                        </Popconfirm>
                      )}
                    </Space>
                  ),
                },
              ]}
              dataSource={roles}
              rowKey="id"
              loading={loadingRoles}
              pagination={{ pageSize: 20, showTotal: (t) => `Total ${t} roles` }}
            />
          </TabPane>

          {isAdmin && (
          <TabPane
            tab={
              <span>
                <ApartmentOutlined style={{ marginRight: 4 }} />
                Companies ({companies.length})
              </span>
            }
            key="6"
          >
            <Input
              placeholder="Search companies by name..."
              prefix={<SearchOutlined />}
              value={companySearchTerm}
              onChange={(e) => setCompanySearchTerm(e.target.value)}
              style={{ marginBottom: 16, maxWidth: 400 }}
              allowClear
            />
            <Table
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
                { title: 'Description', dataIndex: 'description', key: 'description', render: (v?: string) => v || '-' },
                {
                  title: 'Active',
                  dataIndex: 'is_active',
                  key: 'is_active',
                  width: 90,
                  render: (v: boolean) => v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 370,
                  render: (_: any, c: Company) => (
                    <Space>
                      <Button type="link" size="small" onClick={() => handleViewCompanyUsers(c)}>
                        View Users
                      </Button>
                      <Button type="link" size="small" onClick={() => handleViewCompanyBusinesses(c)}>
                        View Businesses
                      </Button>
                      <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditCompany(c)}>
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this company?"
                        description="Linked users/businesses will keep null company."
                        onConfirm={() => deleteCompanyMutation.mutate(c.id)}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                      >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
              dataSource={filteredCompanies}
              rowKey="id"
              loading={loadingCompanies}
              pagination={{ pageSize: 20, showTotal: (t) => `Total ${t} companies` }}
            />
          </TabPane>
          )}
        </Tabs>

        {/* Create User Modal */}
        <Modal
          title="Create New User"
          open={createUserModal}
          onCancel={() => {
            setCreateUserModal(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
          okText="Create User"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateUser}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="first_name"
                label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="last_name"
                label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="send_credentials"
              valuePropName="checked"
              initialValue={true}
              style={{ marginBottom: 12 }}
            >
              <Checkbox>Send username and temporary password by email</Checkbox>
            </Form.Item>

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="A secure temporary password is generated automatically. The user will be required to change it at first login."
            />

            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
              initialValue="agent"
            >
              <Select
                placeholder="Select role"
                loading={loadingRoles}
                showSearch
                optionFilterProp="label"
                options={roles.map((r: Role) => ({ value: r.name, label: r.display_name || r.name }))}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                ['resellers', 'agent_partner'].includes(getFieldValue('role')) ? (
                  <>
                    <Form.Item
                      name="company_ids"
                      label="Companies"
                      rules={[{ required: true, message: 'Select at least one company' }]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select companies"
                        loading={loadingCompanies}
                        showSearch
                        optionFilterProp="label"
                        options={companies
                          .filter((c: Company) => c.is_active)
                          .map((c: Company) => ({ value: c.id, label: c.name }))}
                      />
                    </Form.Item>
                    <Form.Item name="company_name" label="Company Name">
                      <Input />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                      <Select
                        placeholder="Select country"
                        allowClear
                        options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                        onChange={() => form.setFieldValue('state', undefined)}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.role !== curr.role}>
                      {({ getFieldValue }) => {
                        const country = getFieldValue('country');
                        const states = getStatesByCountry(country);
                        return (
                          <Form.Item name="state" label="State">
                            <Select
                              placeholder={country ? 'Select state' : 'Select country first'}
                              allowClear
                              disabled={!country}
                              options={states.map((s) => ({ value: s.code, label: s.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item name="county" label="County">
                      <Input placeholder="County (optional)" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'sales_rep_associated' ? (
                  <>
                    <Form.Item
                      name="parent_agent_partner_id"
                      label="Parent Company"
                      rules={[{ required: true, message: 'Select the parent company' }]}
                    >
                      <Select
                        placeholder="Select parent company"
                        showSearch
                        optionFilterProp="label"
                        options={parentCompanyOptions}
                        onChange={(value) => {
                          const parent = agentPartnerUsers.find((u) => u.id === value);
                          if (parent) form.setFieldValue('company_name', getParentCompanyName(parent) || '');
                          form.setFieldValue('company_ids', []);
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(p, c) =>
                        p.parent_agent_partner_id !== c.parent_agent_partner_id || p.role !== c.role
                      }
                    >
                      {({ getFieldValue }) => {
                        if (getFieldValue('role') !== 'sales_rep_associated') return null;
                        const pid = getFieldValue('parent_agent_partner_id');
                        const parent = agentPartnerUsers.find((u) => u.id === pid);
                        const allowedIds = parent ? getLinkedCompanyIds(parent) : [];
                        const opts = companies.filter(
                          (c) => c.is_active && allowedIds.includes(c.id)
                        );
                        return (
                          <Form.Item
                            name="company_ids"
                            label="Companies (under parent)"
                            rules={[{ required: true, message: 'Select at least one company' }]}
                          >
                            <Select
                              mode="multiple"
                              placeholder={pid ? 'Select companies' : 'Select parent first'}
                              disabled={!pid || !opts.length}
                              showSearch
                              optionFilterProp="label"
                              options={opts.map((c) => ({ value: c.id, label: c.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Sales Rep - Associated users are linked under an Agent/Partner company."
                    />
                    <Form.Item name="company_name" label="Company (from parent)">
                      <Input disabled placeholder="Auto from Agent/Partner" />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                      <Select
                        placeholder="Select country"
                        allowClear
                        options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                        onChange={() => form.setFieldValue('state', undefined)}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.role !== curr.role}>
                      {({ getFieldValue }) => {
                        const country = getFieldValue('country');
                        const states = getStatesByCountry(country);
                        return (
                          <Form.Item name="state" label="State">
                            <Select
                              placeholder={country ? 'Select state' : 'Select country first'}
                              allowClear
                              disabled={!country}
                              options={states.map((s) => ({ value: s.code, label: s.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item name="county" label="County">
                      <Input placeholder="County (optional)" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                const showDeptAndRingCentral = role && !['resellers', 'agent_partner', 'sales_rep_internal'].includes(role);
                return showDeptAndRingCentral ? (
                  <Form.Item name="department_ids" label="Departments">
                    <Select mode="multiple" placeholder="Select departments">
                      {departments.map((dept: Department) => (
                        <Select.Option key={dept.id} value={dept.id}>
                          {dept.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'agent' ? (
                  <Form.Item
                    name="agent_level"
                    label="Agent Level"
                    initialValue={1}
                    tooltip="Seniority for ticket escalation. Level 1 (basic) to Level 3 (expert). Only agents have levels."
                  >
                    <Select>
                      <Select.Option value={1}>Level 1</Select.Option>
                      <Select.Option value={2}>Level 2</Select.Option>
                      <Select.Option value={3}>Level 3</Select.Option>
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                const showRingCentral = role && !['resellers', 'agent_partner', 'sales_rep_internal'].includes(role);
                return showRingCentral ? (
                  <Form.Item name="ringcentral_extension" label="RingCentral Extension">
                    <Input />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit User Modal */}
        <Modal
          title="Edit User"
          open={editUserModal}
          onCancel={() => {
            setEditUserModal(false);
            setSelectedUser(null);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
          okText="Update User"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdateUser}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="first_name"
                label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="last_name"
                label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>

            <Form.Item name="password" label="New Password (leave blank to keep current)">
              <Input.Password />
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
            >
              <Select
                placeholder="Select role"
                loading={loadingRoles}
                showSearch
                optionFilterProp="label"
                options={roles.map((r: Role) => ({ value: r.name, label: r.display_name || r.name }))}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                ['resellers', 'agent_partner'].includes(getFieldValue('role')) ? (
                  <>
                    <Form.Item
                      name="company_ids"
                      label="Companies"
                      rules={[{ required: true, message: 'Select at least one company' }]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select companies"
                        loading={loadingCompanies}
                        showSearch
                        optionFilterProp="label"
                        options={companies
                          .filter((c: Company) => c.is_active)
                          .map((c: Company) => ({ value: c.id, label: c.name }))}
                      />
                    </Form.Item>
                    <Form.Item name="company_name" label="Company Name">
                      <Input />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                      <Select
                        placeholder="Select country"
                        allowClear
                        options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                        onChange={() => form.setFieldValue('state', undefined)}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.role !== curr.role}>
                      {({ getFieldValue }) => {
                        const country = getFieldValue('country');
                        const states = getStatesByCountry(country);
                        return (
                          <Form.Item name="state" label="State">
                            <Select
                              placeholder={country ? 'Select state' : 'Select country first'}
                              allowClear
                              disabled={!country}
                              options={states.map((s) => ({ value: s.code, label: s.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item name="county" label="County">
                      <Input placeholder="County (optional)" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'sales_rep_associated' ? (
                  <>
                    <Form.Item
                      name="parent_agent_partner_id"
                      label="Parent Company"
                      rules={[{ required: true, message: 'Select the parent company' }]}
                    >
                      <Select
                        placeholder="Select parent company"
                        showSearch
                        optionFilterProp="label"
                        options={parentCompanyOptions}
                        onChange={(value) => {
                          const parent = agentPartnerUsers.find((u) => u.id === value);
                          if (parent) form.setFieldValue('company_name', getParentCompanyName(parent) || '');
                          form.setFieldValue('company_ids', []);
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(p, c) =>
                        p.parent_agent_partner_id !== c.parent_agent_partner_id || p.role !== c.role
                      }
                    >
                      {({ getFieldValue }) => {
                        if (getFieldValue('role') !== 'sales_rep_associated') return null;
                        const pid = getFieldValue('parent_agent_partner_id');
                        const parent = agentPartnerUsers.find((u) => u.id === pid);
                        const allowedIds = parent ? getLinkedCompanyIds(parent) : [];
                        const opts = companies.filter(
                          (c) => c.is_active && allowedIds.includes(c.id)
                        );
                        return (
                          <Form.Item
                            name="company_ids"
                            label="Companies (under parent)"
                            rules={[{ required: true, message: 'Select at least one company' }]}
                          >
                            <Select
                              mode="multiple"
                              placeholder={pid ? 'Select companies' : 'Select parent first'}
                              disabled={!pid || !opts.length}
                              showSearch
                              optionFilterProp="label"
                              options={opts.map((c) => ({ value: c.id, label: c.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Sales Rep - Associated users are linked under an Agent/Partner company."
                    />
                    <Form.Item name="company_name" label="Company (from parent)">
                      <Input disabled placeholder="Auto from Agent/Partner" />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                      <Select
                        placeholder="Select country"
                        allowClear
                        options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                        onChange={() => form.setFieldValue('state', undefined)}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.role !== curr.role}>
                      {({ getFieldValue }) => {
                        const country = getFieldValue('country');
                        const states = getStatesByCountry(country);
                        return (
                          <Form.Item name="state" label="State">
                            <Select
                              placeholder={country ? 'Select state' : 'Select country first'}
                              allowClear
                              disabled={!country}
                              options={states.map((s) => ({ value: s.code, label: s.name }))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item name="county" label="County">
                      <Input placeholder="County (optional)" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                const showDeptAndRingCentral = role && !['resellers', 'agent_partner', 'sales_rep_internal'].includes(role);
                return showDeptAndRingCentral ? (
                  <Form.Item name="department_ids" label="Departments">
                    <Select mode="multiple" placeholder="Select departments">
                      {departments.map((dept: Department) => (
                        <Select.Option key={dept.id} value={dept.id}>
                          {dept.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'agent' ? (
                  <Form.Item
                    name="agent_level"
                    label="Agent Level"
                    tooltip="Seniority for ticket escalation. Level 1 (basic) to Level 3 (expert). Only agents have levels."
                  >
                    <Select>
                      <Select.Option value={1}>Level 1</Select.Option>
                      <Select.Option value={2}>Level 2</Select.Option>
                      <Select.Option value={3}>Level 3</Select.Option>
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                const showRingCentral = role && !['resellers', 'agent_partner', 'sales_rep_internal'].includes(role);
                return showRingCentral ? (
                  <Form.Item name="ringcentral_extension" label="RingCentral Extension">
                    <Input />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Create Department Modal */}
        <Modal
          title="Create Department"
          open={createDeptModal}
          onCancel={() => {
            setCreateDeptModal(false);
            deptForm.resetFields();
          }}
          onOk={() => deptForm.submit()}
          okText="Create Department"
        >
          <Form
            form={deptForm}
            layout="vertical"
            onFinish={handleCreateDept}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="name"
              label="Department Name"
              rules={[{ required: true, message: 'Please enter department name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>

        {/* Create Role Modal */}
        <Modal
          title="Create Role"
          open={createRoleModal}
          onCancel={() => {
            setCreateRoleModal(false);
            roleForm.resetFields();
          }}
          onOk={() => roleForm.submit()}
          okText="Create Role"
          confirmLoading={createRoleMutation.isLoading}
        >
          <Form
            form={roleForm}
            layout="vertical"
            onFinish={handleCreateRole}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="name"
              label="Role Name (internal)"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="e.g. support_lead" />
            </Form.Item>

            <Form.Item name="display_name" label="Display Name" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="e.g. Support Lead" />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} placeholder="Optional" />
            </Form.Item>

            <Alert
              type="info"
              showIcon
              message="Permissions are managed from Users > Permissions tab."
              style={{ marginBottom: 12 }}
            />

            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Role Modal */}
        <Modal
          title="Edit Role"
          open={editRoleModal}
          onCancel={() => {
            setEditRoleModal(false);
            setSelectedRole(null);
            roleForm.resetFields();
          }}
          onOk={() => roleForm.submit()}
          okText="Save"
          confirmLoading={updateRoleMutation.isLoading}
        >
          {selectedRole && (
            <Form
              form={roleForm}
              layout="vertical"
              onFinish={handleUpdateRole}
              style={{ marginTop: 16 }}
            >
              <Form.Item label="Role Name (internal)">
                <Input value={selectedRole.name} disabled style={{ fontFamily: 'monospace' }} />
              </Form.Item>

              <Form.Item name="display_name" label="Display Name" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>

              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} />
              </Form.Item>

              <Alert
                type="info"
                showIcon
                message="Permissions are managed from Users > Permissions tab."
                style={{ marginBottom: 12 }}
              />

              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Form>
          )}
        </Modal>

        {/* Create Company Modal */}
        <Modal
          title="Create Company"
          open={createCompanyModal}
          onCancel={() => {
            setCreateCompanyModal(false);
            companyForm.resetFields();
          }}
          onOk={() => companyForm.submit()}
          okText="Create Company"
          confirmLoading={createCompanyMutation.isLoading}
        >
          <Form
            form={companyForm}
            layout="vertical"
            onFinish={handleCreateCompany}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="name"
              label="Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input placeholder="e.g. North Star Processing" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} placeholder="Optional" />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Company Modal */}
        <Modal
          title="Edit Company"
          open={editCompanyModal}
          onCancel={() => {
            setEditCompanyModal(false);
            setSelectedCompany(null);
            companyForm.resetFields();
          }}
          onOk={() => companyForm.submit()}
          okText="Save"
          confirmLoading={updateCompanyMutation.isLoading}
        >
          {selectedCompany && (
            <Form
              form={companyForm}
              layout="vertical"
              onFinish={handleUpdateCompany}
              style={{ marginTop: 16 }}
            >
              <Form.Item
                name="name"
                label="Company Name"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Form>
          )}
        </Modal>

        <Modal
          title={`Company Users${selectedCompanyForUsers ? ` - ${selectedCompanyForUsers.name}` : ''}`}
          open={companyUsersModal}
          onCancel={() => {
            setCompanyUsersModal(false);
            setSelectedCompanyForUsers(null);
            setUserToAddToCompany(undefined);
            setUserSearchTerm('');
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setCompanyUsersModal(false);
                setSelectedCompanyForUsers(null);
                setUserToAddToCompany(undefined);
                setUserSearchTerm('');
              }}
            >
              Close
            </Button>,
          ]}
          width={920}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
            <Text type="secondary">Add a user to this company or remove existing links below.</Text>
            <Space wrap style={{ width: '100%' }}>
              <Select
                showSearch
                allowClear
                placeholder="Search users by name or email"
                style={{ minWidth: 520 }}
                value={userToAddToCompany}
                loading={loadingCompanyUserPicker}
                filterOption={false}
                onSearch={(value) => setUserSearchTerm(value)}
                onChange={(value) => setUserToAddToCompany(value)}
                notFoundContent={
                  companyUserPickerError
                    ? 'Failed to load users'
                    : loadingCompanyUserPicker
                      ? 'Loading users...'
                      : 'No users found'
                }
                options={companyUserPickerOptions}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!userToAddToCompany}
                loading={addCompanyUserMutation.isLoading}
                onClick={handleAddUserToCompany}
              >
                Add User
              </Button>
            </Space>
          </Space>
          <Table
            rowKey="id"
            loading={loadingCompanyUsers}
            dataSource={companyUsers}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{ emptyText: 'No users associated with this company' }}
            columns={[
              {
                title: 'First Name',
                dataIndex: 'first_name',
                key: 'first_name',
              },
              {
                title: 'Last Name',
                dataIndex: 'last_name',
                key: 'last_name',
              },
              {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
                render: (value: string) => <Text copyable>{value}</Text>,
              },
              {
                title: 'Role',
                key: 'role',
                width: 180,
                render: (_: unknown, record: CompanyUser) =>
                  record.role_display_name || record.role || '-',
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 120,
                render: (_: unknown, record: CompanyUser) =>
                  record.linked_via_parent ? (
                    <Tooltip title="This user inherits company access from their parent Agent/Partner">
                      <Text type="secondary">Via parent</Text>
                    </Tooltip>
                  ) : (
                    <Popconfirm
                      title="Remove this user from the company?"
                      onConfirm={() => handleRemoveUserFromCompany(record.id)}
                      okText="Remove"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={removeCompanyUserMutation.isLoading}
                      >
                        Remove
                      </Button>
                    </Popconfirm>
                  ),
              },
            ]}
          />
        </Modal>

        <Modal
          title={`Company Businesses${selectedCompanyForBusinesses ? ` - ${selectedCompanyForBusinesses.name}` : ''}`}
          open={companyBusinessesModal}
          onCancel={() => {
            setCompanyBusinessesModal(false);
            setSelectedCompanyForBusinesses(null);
            setBusinessToAddToCompany(undefined);
            setBusinessSearchTerm('');
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setCompanyBusinessesModal(false);
                setSelectedCompanyForBusinesses(null);
                setBusinessToAddToCompany(undefined);
                setBusinessSearchTerm('');
              }}
            >
              Close
            </Button>,
          ]}
          width={1100}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
            <Text type="secondary">Search for a business to add, or remove existing links below.</Text>
            <Space wrap style={{ width: '100%' }}>
              <Select
                showSearch
                allowClear
                placeholder="Type at least 2 characters to search businesses"
                style={{ minWidth: 420 }}
                value={businessToAddToCompany}
                loading={loadingBusinessSearch}
                filterOption={false}
                onSearch={(value) => setBusinessSearchTerm(value)}
                onChange={(value) => setBusinessToAddToCompany(value)}
                notFoundContent={
                  businessSearchTerm.trim().length < 2
                    ? 'Type at least 2 characters to search'
                    : loadingBusinessSearch
                      ? 'Searching...'
                      : 'No businesses found'
                }
                options={companyBusinessPickerOptions.map((option) => ({
                  value: option.value,
                  label: option.address ? `${option.label} — ${option.address}` : option.label,
                }))}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!businessToAddToCompany}
                loading={addCompanyBusinessMutation.isLoading}
                onClick={handleAddBusinessToCompany}
              >
                Add Business
              </Button>
            </Space>
          </Space>
          <Table
            rowKey="id"
            loading={loadingCompanyBusinesses}
            dataSource={companyBusinesses}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{ emptyText: 'No businesses associated with this company' }}
            columns={[
              {
                title: 'Business Name',
                dataIndex: 'business_name',
                key: 'business_name',
                render: (value: string) => <Text strong>{value}</Text>,
              },
              {
                title: 'Address',
                key: 'address',
                width: 320,
                onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }),
                render: (_: unknown, record: CompanyBusiness) => {
                  const fullAddress = formatBusinessAddress(record);
                  return fullAddress ? (
                    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{fullAddress}</span>
                  ) : (
                    '-'
                  );
                },
              },
              {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
                render: (value?: string) => value || '-',
              },
              {
                title: 'Phone',
                dataIndex: 'phone',
                key: 'phone',
                render: (value?: string) => value || '-',
              },
              {
                title: 'Active',
                dataIndex: 'is_active',
                key: 'is_active',
                width: 100,
                render: (value: boolean) => (value ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 120,
                render: (_: unknown, record: CompanyBusiness) => (
                  <Popconfirm
                    title="Remove this business from the company?"
                    description="The business will remain in the system with no company assigned."
                    onConfirm={() => handleRemoveBusinessFromCompany(record.id)}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      loading={removeCompanyBusinessMutation.isLoading}
                    >
                      Remove
                    </Button>
                  </Popconfirm>
                ),
              },
            ]}
          />
        </Modal>

        <Modal
          title={`Role Permissions${viewPermissionsRole ? ` - ${viewPermissionsRole.display_name}` : ''}`}
          open={!!viewPermissionsRole}
          onCancel={() => {
            setViewPermissionsRole(null);
            setPermissionsModalSearch('');
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setViewPermissionsRole(null);
                setPermissionsModalSearch('');
              }}
            >
              Close
            </Button>,
          ]}
          width={720}
        >
          {viewPermissionsRole ? (() => {
            const roleEditLocked = viewPermissionsRole.is_system || viewPermissionsRole.name === 'admin';
            return (
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                {roleEditLocked
                  ? 'This role is system-managed or admin; toggles are read-only here.'
                  : 'Toggle to enable or disable each permission. Changes save immediately for everyone with this role.'}
              </Text>
              <Input
                allowClear
                placeholder="Filter permissions…"
                value={permissionsModalSearch}
                onChange={(e) => setPermissionsModalSearch(e.target.value)}
                style={{ marginBottom: 16, maxWidth: 400 }}
                prefix={<SearchOutlined />}
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {filteredPermissionModalList.map((p: { key: string; label: string }) => (
                  <div
                    key={p.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 12,
                      padding: '8px 12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      cursor: roleEditLocked ? 'default' : updatePermissionsMutation.isLoading ? 'wait' : 'pointer',
                      minHeight: 48,
                    }}
                    onClick={() => {
                      if (roleEditLocked || updatePermissionsMutation.isLoading) return;
                      const cur = viewPermissionsRole.permissions?.[p.key] === true;
                      handlePermissionToggle(viewPermissionsRole.id, p.key, viewPermissionsRole.permissions || {}, !cur);
                    }}
                    role="presentation"
                  >
                    <Text style={{ flex: 1 }}>{p.label}</Text>
                    <Switch
                      checked={viewPermissionsRole.permissions?.[p.key] === true}
                      onChange={(checked) =>
                        handlePermissionToggle(viewPermissionsRole.id, p.key, viewPermissionsRole.permissions || {}, checked)}
                      disabled={roleEditLocked || updatePermissionsMutation.isLoading}
                      onClick={(_, e) => e.stopPropagation()}
                    />
                  </div>
                ))}
              </div>
            </div>
            );
          })() : null}
        </Modal>
      </Content>
      {/* Bulk Upload Modal (match Businesses style) */}
      <Modal
        title="Bulk Upload Users"
        open={bulkUploadOpen}
        onCancel={() => setBulkUploadOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setBulkUploadOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="download"
            type="link"
            href="/templates/user_template.csv"
            download
          >
            Download Template
          </Button>,
        ]}
      >
        <p>Upload a CSV file with user data. Make sure to follow the template format.</p>
        <Upload.Dragger
          accept=".csv"
          beforeUpload={async (file) => {
            const formData = new FormData();
            formData.append('file', file as any);
            try {
              const res = await api.post('/api/users/bulk-upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
              const data = res.data || {};
              if (data.success) message.success(`Uploaded ${data.success} users`);
              if (Array.isArray(data.errors) && data.errors.length) message.error(`${data.errors.length} rows had errors`);
              queryClient.invalidateQueries('users');
              setBulkUploadOpen(false);
            } catch (e: any) {
              message.error(e?.response?.data?.error || 'Failed to upload');
            }
            return false;
          }}
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to upload</p>
          <p className="ant-upload-hint">Only CSV files are supported</p>
        </Upload.Dragger>
      </Modal>

      {/* Confirm Bulk Delete */}
      <Modal
        title={`Confirm Bulk Delete (${selectedRowKeys.length} user(s))`}
        open={confirmBulkOpen}
        onCancel={() => setConfirmBulkOpen(false)}
        onOk={() => { bulkDeleteUsers.mutate(selectedRowKeys as string[]); setConfirmBulkOpen(false); }}
        okButtonProps={{ danger: true, disabled: confirmBulkText !== 'DELETE', children: 'Delete' }}
      >
        <Typography.Paragraph type="secondary">
          This action cannot be undone. Type DELETE to confirm.
        </Typography.Paragraph>
        <Input placeholder="Type DELETE" value={confirmBulkText} onChange={(e) => setConfirmBulkText(e.target.value)} />
      </Modal>

      {/* Confirm Password Reset */}
      <Modal
        title="Send Password Reset Link"
        open={passwordResetConfirm.open}
        onCancel={() => setPasswordResetConfirm({ open: false, user: null })}
        onOk={confirmSendPasswordReset}
        okText="Send Link"
        cancelText="Cancel"
        okButtonProps={{ loading: sendPasswordResetMutation.isLoading }}
      >
        {passwordResetConfirm.user && (
          <div>
            <Typography.Paragraph>
              Are you sure you want to send a password reset link to:
            </Typography.Paragraph>
            <Typography.Paragraph strong style={{ fontSize: 16, marginBottom: 8 }}>
              {passwordResetConfirm.user.first_name} {passwordResetConfirm.user.last_name}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              {passwordResetConfirm.user.email}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
              The reset link will be valid for 24 hours.
            </Typography.Paragraph>
          </div>
        )}
      </Modal>

      {/* Approve User Request Modal */}
      <Modal
        title="Approve User Request"
        open={approveRequestModal}
        onCancel={() => {
          setApproveRequestModal(false);
          approveForm.resetFields();
          setSelectedRequest(null);
        }}
        onOk={() => approveForm.submit()}
        width={600}
        okText="Approve & Create Account"
        okButtonProps={{ loading: approveRequestMutation.isLoading }}
      >
        {selectedRequest && (
          <div style={{ marginBottom: 24 }}>
            <Alert
              message="User Request Details"
              description={
                <div>
                  <p><strong>Name:</strong> {selectedRequest.first_name} {selectedRequest.last_name}</p>
                  <p><strong>Email:</strong> {selectedRequest.email}</p>
                  <p><strong>Phone:</strong> {selectedRequest.phone || 'Not provided'}</p>
                  {selectedRequest.notes && (
                    <p><strong>Notes:</strong> {selectedRequest.notes}</p>
                  )}
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Text type="secondary">
              Fill in the remaining user information below. The user will receive an email to set their password.
            </Text>
          </div>
        )}
        <Form
          form={approveForm}
          layout="vertical"
          onFinish={confirmApproveRequest}
        >
          <Form.Item
            name="role_id"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select
              placeholder="Select role"
              loading={loadingRoles}
              showSearch
              optionFilterProp="label"
              options={(rolesData?.roles || []).map((role: Role) => ({ value: role.id, label: role.display_name || role.name }))}
            />
          </Form.Item>

          <Form.Item name="department_id" label="Department">
            <Select placeholder="Select department" allowClear>
              {departments.map((dept: Department) => (
                <Select.Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="department_ids" label="Departments (Multiple)">
            <Select mode="multiple" placeholder="Select departments">
              {departments.map((dept: Department) => (
                <Select.Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="extension" label="Extension">
            <Input placeholder="Enter extension" />
          </Form.Item>

          <Form.Item name="ringcentral_extension" label="RingCentral Extension">
            <Input placeholder="Enter RingCentral extension" />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Share Temporary Credentials"
        open={tempCredentialsNotice.open}
        onCancel={() => setTempCredentialsNotice({ open: false })}
        onOk={() => setTempCredentialsNotice({ open: false })}
        okText="Done"
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Credentials email was not sent. Share these credentials with the user manually."
          description={tempCredentialsNotice.emailError || undefined}
        />
        <div style={{ marginBottom: 12 }}>
          <Text strong>Username</Text>
          <Input value={tempCredentialsNotice.username} readOnly style={{ marginTop: 6 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Temporary Password</Text>
          <Input.Password value={tempCredentialsNotice.temporaryPassword} readOnly style={{ marginTop: 6 }} />
        </div>
        <Button
          block
          onClick={() => {
            const credentials = `Username: ${tempCredentialsNotice.username}\nTemporary Password: ${tempCredentialsNotice.temporaryPassword}`;
            navigator.clipboard.writeText(credentials);
            message.success('Credentials copied to clipboard');
          }}
        >
          Copy Credentials
        </Button>
      </Modal>

      {/* Reject User Request Modal */}
      <Modal
        title="Reject User Request"
        open={rejectRequestModal}
        onCancel={() => {
          setRejectRequestModal(false);
          setSelectedRequest(null);
        }}
        onOk={() => {
          const reasonInput = document.querySelector('#rejection-reason') as HTMLTextAreaElement;
          confirmRejectRequest(reasonInput?.value || undefined);
        }}
        width={500}
        okText="Reject Request"
        okButtonProps={{ danger: true, loading: rejectRequestMutation.isLoading }}
      >
        {selectedRequest && (
          <div>
            <Alert
              message="Are you sure you want to reject this request?"
              description={
                <div>
                  <p><strong>Name:</strong> {selectedRequest.first_name} {selectedRequest.last_name}</p>
                  <p><strong>Email:</strong> {selectedRequest.email}</p>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div style={{ marginTop: 16 }}>
              <Text strong>Rejection Reason (Optional)</Text>
              <Input.TextArea
                id="rejection-reason"
                rows={3}
                placeholder="Enter reason for rejection (optional)"
                style={{ marginTop: 8 }}
              />
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default UsersAntd;

